import { useEffect, useMemo, useState } from 'react';
import MetricsCsvService from '@avtechsoftware/ra-javascript-utilities/src/utilities/metricsGroups/MetricsCsvService';
import dashboardApi from '../../services/dashboardApi';
import SearchSortType from '../../../../utility/types/SearchSortType';

export const FILTER_MODE = {
  ALL: 'ALL',
  FAVORITED: 'FAVORITED',
  TRIGGERED: 'TRIGGERED',
  SAVED_SEARCH: 'SAVED_SEARCH',
};

// `currentPage` is intentionally NOT in the default context — pagination is
// transient view state and lives with the hook (see viewState below).
export const ALERTS_LIST_DEFAULT_CONTEXT = Object.freeze({
  filter: FILTER_MODE.ALL,
  savedSearchId: null,
  rowsPerPage: 10,
  sortType: SearchSortType.ALERT_NAME,
  sort: 'DES',
});

const fetchSamplesForWidgetPage = async (widgetId, pageNo) => {
  if (widgetId == null) return {};

  const url = dashboardApi.alertsList.buildSamplesUrl(widgetId, pageNo);
  const rows = await MetricsCsvService.loadCsvData(url, (row) => row);

  // Derive channel keys from the CSV header (every column other than "Date").
  const channelKeys = rows && rows.length > 0
    ? Object.keys(rows[0]).filter((k) => k !== 'Date')
    : [];

  const result = {};
  channelKeys.forEach((k) => { result[k] = []; });

  (rows || []).forEach((row) => {
    const ts = Number(row.Date);
    if (!Number.isFinite(ts)) return;
    const time = new Date(ts * 1000);
    channelKeys.forEach((key) => {
      const raw = row[key];
      if (raw === undefined || raw === null || raw === '') return;
      const value = Number(raw);
      if (!Number.isNaN(value)) {
        result[key].push({ time, value });
      }
    });
  });

  // The endpoint returns newest first; d3.line draws in array order, so sort
  // ascending for a left-to-right sparkline.
  Object.keys(result).forEach((k) => {
    result[k].sort((a, b) => a.time - b.time);
  });

  return result;
};

export const useAlertsListWidgetData = (
  context,
  { isEditMode = false, widgetId = null, refreshTick = 0 } = {},
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pageNo, setPageNo] = useState(0);
  const [isDefault, setIsDefault] = useState(false);

  const {
    filter,
    savedSearchId,
    rowsPerPage,
    sortType,
    sort,
  } = { ...ALERTS_LIST_DEFAULT_CONTEXT, ...context };

  // Reset to page 0 whenever the server-side query changes. Runs on mount
  // too, but that's a no-op since pageNo starts at 0.
  useEffect(() => {
    setPageNo(0);
  }, [filter, savedSearchId, rowsPerPage, sortType, sort, widgetId]);

  useEffect(() => {
    if (isEditMode) return undefined;
    if (widgetId == null) {
      setData({
        alerts: [],
        totalResults: 0,
        totalRecordsIdList: [],
        samplesByChannelKey: {},
        trendsLoading: false,
        trendsError: false,
      });
      setIsDefault(false);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    dashboardApi.alertsList
      .getList(widgetId, pageNo)
      .then((response) => {
        if (cancelled) return;
        const body = dashboardApi.unwrapBody(response) || {};
        const searchResult = body.searchResult || [];
        const totalRecords = (body.pageInfo && body.pageInfo.totalRecords) || 0;
        const totalRecordsIdList = body.totalRecordsIdList || [];

        // Subscription-level alert count from metaData decides the "default"
        // (empty-subscription) presentation. Any non-zero count — even if the
        // current page/filter is empty — means the user has real data.
        const subscriptionAlertCount = body.metaData
        && body.metaData.alertCount != null
          ? Number(body.metaData.alertCount)
          : null;
        const nextIsDefault = subscriptionAlertCount === 0;
        setIsDefault(nextIsDefault);

        const hasTrendsToFetch = searchResult.length > 0 && !nextIsDefault;

        setData({
          alerts: searchResult,
          totalResults: totalRecords,
          totalRecordsIdList,
          samplesByChannelKey: {},
          trendsLoading: hasTrendsToFetch,
          trendsError: false,
        });
        setIsLoading(false);

        if (!hasTrendsToFetch) return;

        fetchSamplesForWidgetPage(widgetId, pageNo)
          .then((samplesByChannelKey) => {
            if (cancelled) return;
            setData((prev) => (prev ? {
              ...prev,
              samplesByChannelKey,
              trendsLoading: false,
              trendsError: false,
            } : prev));
          })
          .catch(() => {
            if (cancelled) return;
            setData((prev) => (prev ? {
              ...prev,
              samplesByChannelKey: {},
              trendsLoading: false,
              trendsError: true,
            } : prev));
          });
      })
      .catch(() => {
        if (cancelled) return;
        setData({
          alerts: [],
          totalResults: 0,
          totalRecordsIdList: [],
          samplesByChannelKey: {},
          trendsLoading: false,
          trendsError: false,
        });
        setIsDefault(false);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [widgetId,
    pageNo,
    filter,
    savedSearchId,
    rowsPerPage,
    sortType,
    sort,
    isEditMode,
    refreshTick,
  ]);

  const viewState = useMemo(() => ({ pageNo, setPageNo }), [pageNo]);

  return {
    isLoading, data, viewState, isDefault,
  };
};

export const filterLabel = (ctx) => {
  switch (ctx.filter) {
    case FILTER_MODE.FAVORITED: return 'Favorited';
    case FILTER_MODE.TRIGGERED: return 'Triggered';
    case FILTER_MODE.SAVED_SEARCH: {
      const count = (ctx.savedSearchIds || []).length;
      return count === 1 ? 'the selected saved search' : `${count} saved searches`;
    }
    case FILTER_MODE.ALL:
    default:
      return 'All';
  }
};
